// @gjl/voice — Fugue Lab pricing, codified.
//
// Single source of truth for every price string Fugue Lab shows a prospect:
// the pitch deck (`/deck` skill + `@gjl/pdf` template), and (as it adopts this
// module) the Studio marketing site `/packages` page.
//
// Two things live here:
//
//   1. FESTIVAL DECK TIERS — the live, validated festival go-to-market
//      (Platform / Concierge / Project add-ons). This is what the pitch deck
//      renders today. `deckPricingForBudget()` reproduces the deck's
//      budget->highlight logic. Editing a price here changes the deck.
//
//   2. THE MODULAR CATALOG — the proposed capability-priced model (website /
//      email / donor management / ticketing / Canticle, self-serve packages,
//      done-for-you services, add-ons, verticals). This is DATA, not yet wired
//      into the deck output; it is the codified form of the strategy doc
//      `projects/brands/fugue-lab/docs/strategy/modular-pricing-and-studio-repositioning.md`.
//      Capabilities: website / email / donor management / ticketing / Fugue
//      Lab Music Manager (music library + rehearsal; "Canticle" = church layer).
//      The Studio site imports it; the deck will render selected pieces once
//      the model is signed off (that doc's open decisions).
//
// PURE module: data + string helpers only, no I/O. Safe to import from the
// React client, the deck skill script, and any Lambda alike.
//
// Voice: no em dashes anywhere in the copy strings (hard rule). Use "plus",
// commas, or hyphens for numeric ranges.
/** The canonical three festival tiers, no highlight applied. */
export const FESTIVAL_DECK_TIERS = [
    {
        name: 'Platform',
        price: '$500/mo',
        line: 'Website plus applications plus comms tooling plus support. For teams with their own designer.',
    },
    {
        name: 'Concierge',
        price: '$1,000-1,500/mo',
        line: 'Everything in Platform plus branding, design, and done-for-you launch and content.',
    },
    {
        name: 'Project add-ons',
        price: '$1-3K',
        line: 'Logo, brand refresh, print programs, or a season campaign. One-time, any plan.',
    },
];
export const DECK_PRICING_NOTE = 'No per-applicant fees. No vendor lock-in. Month-to-month, or a rolled annual plan that matches your budget cycle.';
/** The budget above which Concierge is the highlighted tier. */
export const CONCIERGE_BUDGET_THRESHOLD = 1000;
/**
 * The canonical deck pricing block, with `highlight` set by the lead's monthly
 * budget: under the threshold (or an unknown-but-set low budget) highlights
 * Platform; at/above it highlights Concierge; an undefined budget highlights
 * nothing. Reproduces the `/deck` skill's original `pricingForBudget()`.
 */
export function deckPricingForBudget(budget) {
    const highlightConcierge = budget !== undefined && budget >= CONCIERGE_BUDGET_THRESHOLD;
    const highlightPlatform = budget !== undefined && !highlightConcierge;
    return {
        tiers: FESTIVAL_DECK_TIERS.map((t) => {
            if (t.name === 'Platform')
                return { ...t, highlight: highlightPlatform };
            if (t.name === 'Concierge')
                return { ...t, highlight: highlightConcierge };
            return { ...t };
        }),
        note: DECK_PRICING_NOTE,
    };
}
/** The core self-serve capabilities. Donations = free to *accept* (0% platform
 *  fee); the paid capability is donor MANAGEMENT (recurring, receipts, CRM). */
export const CAPABILITIES = [
    {
        id: 'website',
        name: 'Website',
        priceMonthly: 25,
        note: 'Hosting, editor, custom domain, forms, SEO.',
        selfServeLive: true,
    },
    {
        id: 'email',
        name: 'Email Marketing',
        priceMonthly: 20,
        note: 'Newsletters, sequences, automations. Up to 2,000 contacts; $35/mo up to 10,000. No send metering.',
        selfServeLive: true,
    },
    {
        id: 'donor-management',
        name: 'Donor Management',
        priceMonthly: 25,
        note: 'Recurring giving, automated tax receipts, campaigns, donor CRM. Accepting donations is free (0% platform fee); this is the management layer.',
        selfServeLive: false,
    },
    {
        id: 'ticketing',
        name: 'Ticketing',
        priceMonthly: 25,
        perUnitFee: '1.5% per ticket',
        note: 'Selling a ticket is free apart from the 1.5%; the monthly price is box-office management: tiers, capacity, QR check-in, reporting.',
        selfServeLive: false,
    },
    {
        id: 'music-manager',
        name: 'Fugue Lab Music Manager',
        priceMonthly: 30,
        proMonthly: 75,
        note: 'Music library, rehearsal planning, and ensemble/roster management for any ensemble. Pro adds attendance, multi-ensemble, sub management, automations. The church-specific liturgy/lectionary layer is branded "Canticle". The ensemble-vertical anchor.',
        selfServeLive: false,
    },
];
export const SELF_SERVE_PACKAGES = [
    { id: 'foundation', name: 'Foundation', includes: ['website'], priceMonthly: 25, saveVsAlaCarte: 0 },
    { id: 'connect', name: 'Connect', includes: ['website', 'email'], priceMonthly: 40, saveVsAlaCarte: 5 },
    {
        id: 'advance',
        name: 'Advance',
        includes: ['website', 'email', 'donor-management'],
        priceMonthly: 60,
        saveVsAlaCarte: 10,
    },
    {
        id: 'complete',
        name: 'Complete',
        includes: ['website', 'email', 'donor-management', 'ticketing'],
        priceMonthly: 75,
        saveVsAlaCarte: 20,
        mostPopular: true,
    },
];
export const MANAGED_SERVICES = [
    {
        id: 'website-care',
        name: 'Website Care',
        priceMonthly: 100,
        note: 'One update request per month, 72-hour turnaround. Website software included.',
    },
    {
        id: 'managed-website',
        name: 'Managed Website',
        priceMonthly: 200,
        note: 'Unlimited update requests, one active at a time, 72-hour turnaround.',
    },
    {
        id: 'managed-email',
        name: 'Managed Email',
        priceMonthly: 350,
        note: 'We plan, write, design, and send. About 2 campaigns per month plus 1 automation.',
    },
];
export const DFY_BUNDLES = [
    {
        id: 'growth-partner',
        name: 'Growth Partner',
        price: '$500/mo',
        note: 'Managed Website plus Managed Email, $50 under the two separately.',
    },
    {
        id: 'concierge',
        name: 'Concierge',
        price: '$1,000-1,500/mo',
        note: 'Everything done for you across all capabilities, plus strategy. Fundraising and box office run for you live here, not as standalone services.',
    },
];
export const FESTIVAL_LANE = [
    {
        id: 'festival-platform',
        name: 'Festival Platform',
        price: '$500/mo',
        note: 'Website plus Applications plus Email plus Ticketing, light-managed.',
    },
    {
        id: 'festival-concierge',
        name: 'Festival Concierge',
        price: '$1,000-1,500/mo',
        note: 'Festival Platform plus branding, done-for-you launch, and a design retainer.',
    },
];
export const CONSULTING = [
    {
        id: 'strategy-intensive',
        name: 'Digital Strategy Intensive',
        price: '$1,500-3,000 one-time',
        note: 'Audit plus roadmap plus capability setup.',
    },
    {
        id: 'fractional-director',
        name: 'Fractional Digital Director',
        price: '$500-1,500/mo',
        note: 'Ongoing strategy, oversight, prioritization.',
    },
];
/** One-time builds and small recurring add-ons. */
export const ONE_TIME_AND_ADDONS = [
    {
        id: 'launch-build',
        name: 'Launch build',
        price: '$250 one-time',
        note: 'Custom-designed site plus basic integrations. DNS/domain connection included. Doubles as the done-for-you onboarding fee.',
    },
    {
        id: 'bespoke-build',
        // "Bespoke" is internal-only vocabulary (voice rule: never in customer
        // copy). Any customer-facing render of this entry must display a
        // different label, e.g. "Custom build".
        name: 'Bespoke build',
        price: 'Quoted',
        note: 'Fully custom design, complex integrations, festival/multi-program work.',
    },
    {
        id: 'managed-domain',
        name: 'Managed Domain',
        price: '$25/yr',
        note: 'We register plus host in Route 53 and handle renewals. Registrant is always the client, transferable on request. No lock-in.',
    },
    {
        id: 'professional-email',
        name: 'Professional Email',
        price: '$5/mo',
        note: 'name@theirorg forwards to their existing inbox plus send-as. Free with any build.',
    },
    {
        id: 'workspace-setup',
        name: 'Workspace mailbox setup',
        price: '$50-150 one-time',
        note: 'One-time Google Workspace setup. The client owns the account and pays Google directly.',
    },
    {
        id: 'migration-heavy',
        name: 'Data migration (heavy)',
        price: '$150-500 one-time',
        note: 'Large donor/member/history imports. Simple content migration is bundled into a build. Offboarding export is always free.',
    },
];
export const VERTICAL_STACKS = [
    {
        id: 'church',
        name: 'Churches / parishes',
        anchor: 'music-manager',
        stack: ['website', 'email', 'donor-management', 'music-manager'],
        note: 'Anchored by Fugue Lab Music Manager with the Canticle liturgy layer. Professional Email is a common add-on.',
    },
    {
        id: 'festival',
        name: 'Festivals',
        anchor: 'applications',
        stack: ['website', 'email', 'ticketing', 'donor-management'],
        note: 'Anchored by Applications (auditions). Sold as the $500/mo Festival Platform lane.',
    },
];
/** Annual prepay: pay for 10 months, get 12 (about 17% off). Applies to every
 *  self-serve and done-for-you monthly plan. Per-unit/usage fees unaffected. */
export const ANNUAL_PREPAY_MONTHS_FREE = 2;
/** Convenience: a capability by id. */
export function capability(id) {
    return CAPABILITIES.find((c) => c.id === id);
}
/** Sum of the core self-serve capabilities at a-la-carte price (excludes the
 *  Music Manager vertical add-on), for "save vs a la carte" math on packages. */
export function alaCarteCoreMonthly() {
    return CAPABILITIES.filter((c) => c.id !== 'music-manager').reduce((sum, c) => sum + c.priceMonthly, 0);
}
